import { useEffect, useId, useState, type ReactNode, type RefObject } from "react";
import { ApiError } from "@/api/client";

export const inputClass =
  "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand aria-invalid:border-red-400";

export const submitClass =
  "rounded-md border border-brand-strong bg-brand-strong px-3 py-2 text-sm font-medium text-white transition hover:bg-brand-stronger disabled:cursor-not-allowed disabled:opacity-50";

/** A labelled control; its problem, if any, is the control's accessible description. */
export function Field({
  label,
  problem,
  hint,
  children,
}: {
  label: string;
  problem?: string;
  hint?: ReactNode;
  children: (props: { id: string; "aria-invalid"?: true; "aria-describedby"?: string }) => ReactNode;
}) {
  const id = useId();
  const problemId = `${id}-problem`;
  return (
    <div className="text-sm">
      <label htmlFor={id} className="font-medium text-zinc-700">
        {label}
      </label>
      {children({ id, ...(problem ? { "aria-invalid": true, "aria-describedby": problemId } : {}) })}
      {problem ? (
        <p id={problemId} className="mt-1 text-xs text-red-700">
          {problem}
        </p>
      ) : null}
      {hint ? <p className="mt-1 text-xs text-zinc-500">{hint}</p> : null}
    </div>
  );
}

/** Problems show once their field is left or the form submitted, not while it is still blank. */
export function useRevealProblems<F extends string>(form: RefObject<HTMLFormElement | null>) {
  const [touched, setTouched] = useState<ReadonlySet<F>>(() => new Set());
  const [attempts, setAttempts] = useState(0);
  useEffect(() => {
    if (attempts > 0) form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [attempts, form]);
  return {
    shown: (field: F, problem: string | undefined) => (attempts > 0 || touched.has(field) ? problem : undefined),
    touch: (field: F) => setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field))),
    submitted: () => setAttempts((n) => n + 1),
  };
}

/** Splits a 422's details into those naming one of `fields` and the rest, as text. */
export function serverProblems(error: unknown, fields: readonly string[]) {
  const byField: Partial<Record<string, string>> = {};
  const other: string[] = [];
  const details = error instanceof ApiError && error.status === 422 ? error.details : [];
  for (const detail of details) {
    if (detail.field && fields.includes(detail.field)) byField[detail.field] = detail.issue;
    else other.push(detail.field ? `${detail.field}: ${detail.issue}` : (detail.issue ?? ""));
  }
  return { byField, other };
}

export function OtherProblems({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <ul className="list-disc pl-5 text-sm text-red-700">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}
